import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { ScrollArea } from "./ui/scroll-area";
import MessageBubble from "./MessageBubble";
import { Send, Loader2, Paperclip, X, FileText, Mic, MicOff, Volume2, Image } from "lucide-react";

// Using relative URL for API calls
const API = "/api";

// File size limits
const FREE_PLAN_LIMIT = 5 * 1024 * 1024; // 5MB
const PRO_PLAN_LIMIT = 25 * 1024 * 1024; // 25MB

// Allowed file types
const ALLOWED_TYPES = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "text/plain": "TXT",
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/jpg": "JPG",
  "image/webp": "WEBP",
  "image/gif": "GIF",
};

// Check if file type is an image
const isImageType = (type) => {
  return type?.startsWith("image/");
};

export default function ChatArea({
  conversation,
  messages,
  setMessages,
  onNewChat,
  updateConversationTitle,
  refreshConversations,
  setCurrentConversation,
  user,
}) {
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Voice state
  const [voiceMode, setVoiceMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Check if user is on Pro plan
  const isProPlan = user?.plan?.toLowerCase()?.includes("pro");
  const maxFileSize = isProPlan ? PRO_PLAN_LIMIT : FREE_PLAN_LIMIT;
  const maxFileSizeMB = maxFileSize / (1024 * 1024);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  // Get auth token
  const getAuthToken = () => localStorage.getItem("auth_token");

  // Handle input change
  const handleInputChange = (e) => {
    setInput(e.target.value);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_TYPES[file.type]) {
      toast.error("File type not supported. Please upload PDF, DOCX, TXT, or image files (PNG, JPG, WEBP, GIF).");
      return;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      toast.error(`File too large. Maximum size is ${maxFileSizeMB}MB for your plan.`);
      return;
    }

    // Upload file
    setUploadingFile(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/files/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
        
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to upload file");
      }

      const data = await response.json();
      setAttachedFile({
        file_id: data.file_id,
        filename: data.filename,
        file_size: data.file_size,
        file_type: data.file_type || "document",
        content_type: data.content_type,
        text_extracted: data.text_extracted,
      });
      toast.success(data.file_type === "image" ? "Image uploaded and analyzed" : "File uploaded successfully");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingFile(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Remove attached file
  const handleRemoveFile = () => {
    setAttachedFile(null);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Toggle voice mode
  const toggleVoiceMode = () => {
    if (voiceMode) {
      // Turning off - stop any recording
      if (isRecording) {
        stopRecording();
      }
      // Stop any playing audio
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current = null;
      }
    }
    setVoiceMode(!voiceMode);
    if (!voiceMode) {
      toast.success("Voice mode enabled. Click the mic to speak.");
    } else {
      toast.info("Voice mode disabled.");
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      toast.error("Could not access microphone. Please check permissions.");
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Transcribe audio
  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");

      const response = await fetch(`${API}/voice/transcribe`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
        
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Transcription failed");
      }

      const data = await response.json();
      if (data.text && data.text.trim()) {
        setInput(data.text.trim());
        // Auto-send in voice mode
        if (voiceMode) {
          // Small delay to show the transcribed text
          setTimeout(() => {
            handleSendWithVoice(data.text.trim());
          }, 500);
        }
      } else {
        toast.error("Could not understand audio. Please try again.");
      }
    } catch (error) {
      console.error("Transcription error:", error);
      toast.error(error.message || "Failed to transcribe audio");
    } finally {
      setIsTranscribing(false);
    }
  };

  // Speak text using TTS
  const speakText = useCallback(async (text) => {
    if (!voiceMode || !text) return;
    
    setIsSpeaking(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${API}/voice/synthesize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text, voice: "nova" }),
        
      });

      if (!response.ok) {
        throw new Error("Speech synthesis failed");
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Play audio
      const audio = new Audio(audioUrl);
      audioPlayerRef.current = audio;
      
      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        audioPlayerRef.current = null;
      };
      
      audio.onerror = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        audioPlayerRef.current = null;
      };
      
      await audio.play();
    } catch (error) {
      console.error("TTS error:", error);
      setIsSpeaking(false);
    }
  }, [voiceMode]);

  // Handle send with voice response
  const handleSendWithVoice = async (textToSend) => {
    const messageContent = textToSend || input.trim();
    if (!messageContent || isStreaming) return;
    
    // Clear input immediately
    setInput("");
    
    // Call regular send and capture response for TTS
    await handleSendInternal(messageContent, true);
  };

  // Internal send function
  const handleSendInternal = async (messageContent, shouldSpeak = false) => {
    if (!messageContent && !attachedFile) return;
    if (isStreaming) return;

    let currentConversationId = conversation?.conversation_id;

    // Create conversation if needed
    if (!currentConversationId) {
      try {
        const token = getAuthToken();
        const response = await fetch(`${API}/conversations`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ title: "New Chat" }),
          
        });

        if (!response.ok) {
          // Check for conversation limit error
          if (response.status === 403) {
            try {
              const errorData = await response.json();
              if (errorData.detail?.error === "conversation_limit_reached") {
                toast.error(
                  "You've reached your conversation limit. Upgrade to Pro or purchase Plus credits to continue.",
                  { duration: 6000 }
                );
                return;
              }
            } catch (e) {
              // JSON parsing failed, show generic limit message
              toast.error(
                "You've reached your conversation limit. Please upgrade your plan.",
                { duration: 6000 }
              );
              return;
            }
          }
          throw new Error("Failed to create conversation");
        }

        const newConv = await response.json();
        currentConversationId = newConv.conversation_id;
        
        // Set the current conversation so subsequent messages use it
        if (setCurrentConversation) {
          setCurrentConversation(newConv);
        }
        
        await refreshConversations();
      } catch (error) {
        toast.error("Failed to start conversation");
        return;
      }
    }

    const finalContent = messageContent || (attachedFile ? `Please analyze this document: ${attachedFile.filename}` : "");
    
    const userMessage = {
      message_id: `temp_${Date.now()}`,
      conversation_id: currentConversationId,
      role: "user",
      content: finalContent,
      file_info: attachedFile ? { filename: attachedFile.filename, file_id: attachedFile.file_id } : null,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const fileToSend = attachedFile;
    setAttachedFile(null);
    setIsStreaming(true);
    setStreamingContent("");

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    let fullResponseText = "";

    try {
      const token = getAuthToken();
      const response = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: finalContent,
          conversation_id: currentConversationId,
          file_id: fileToSend?.file_id || null,
        }),
        
      });

      // Check for conversation limit error
      if (response.status === 403) {
        try {
          const errorData = await response.json();
          if (errorData.detail?.error === "conversation_limit_reached") {
            // Remove the user message we just added
            setMessages((prev) => prev.slice(0, -1));
            
            // Show ZOEY's apologetic message as an assistant message
            const limitMessage = {
              message_id: `limit_${Date.now()}`,
              conversation_id: currentConversationId,
              role: "assistant",
              content: "I'm so sorry, but you've used all your conversations this month! 😔 I'd really love to continue helping you with your business challenges.\n\nTo keep our conversation going, you can:\n\n• **Upgrade to Pro** for unlimited conversations ($29/month)\n• **Purchase Plus credits** to add more conversations ($1 each)\n\nJust click on your profile at the bottom of the sidebar and select 'Upgrade Plan' to continue. I'll be here waiting! 💚",
              timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, limitMessage]);
            setIsStreaming(false);
            return;
          }
        } catch (e) {
          // JSON parsing failed, show generic limit message in chat
          setMessages((prev) => prev.slice(0, -1));
          const limitMessage = {
            message_id: `limit_${Date.now()}`,
            conversation_id: currentConversationId,
            role: "assistant",
            content: "I'm sorry, but you've reached your conversation limit for this month. To continue chatting, please upgrade to Pro or purchase additional conversations. Click on your profile in the sidebar and select 'Upgrade Plan'. 💚",
            timestamp: new Date().toISOString(),
          };
          setMessages((prev) => [...prev, limitMessage]);
          setIsStreaming(false);
          return;
        }
        throw new Error("Access denied");
      }

      if (!response.ok) throw new Error("Failed to send message");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === "start") {
                setStreamingMessageId(data.message_id);
              } else if (data.type === "chunk") {
                fullResponseText += data.content;
                setStreamingContent(fullResponseText);
              } else if (data.type === "end") {
                // Add the complete message
                const aiMessage = {
                  message_id: streamingMessageId || `msg_${Date.now()}`,
                  conversation_id: currentConversationId,
                  role: "assistant",
                  content: fullResponseText,
                  timestamp: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, aiMessage]);
                setStreamingContent("");
                setStreamingMessageId(null);
                
                // Speak the response if in voice mode
                if (shouldSpeak && voiceMode && fullResponseText) {
                  speakText(fullResponseText);
                }
                
                // Update conversation title if first message
                if (messages.length === 0) {
                  const title = finalContent.slice(0, 50) + (finalContent.length > 50 ? "..." : "");
                  updateConversationTitle(currentConversationId, title);
                  await refreshConversations();
                }
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      toast.error("Failed to get response");
      console.error("Stream error:", error);
    } finally {
      setIsStreaming(false);
    }
  };

  // Send message (wrapper)
  const handleSend = async () => {
    if ((!input.trim() && !attachedFile) || isStreaming) return;
    const messageContent = input.trim() || (attachedFile ? `Please analyze this document: ${attachedFile.filename}` : "");
    setInput("");
    await handleSendInternal(messageContent, voiceMode);
  };

  // Regenerate response
  const handleRegenerate = async (messageId) => {
    if (isStreaming) return;

    setIsStreaming(true);
    setStreamingContent("");

    // Remove the AI message being regenerated
    setMessages((prev) => prev.filter((m) => m.message_id !== messageId));

    let fullResponseText = "";

    try {
      const token = getAuthToken();
      const response = await fetch(`${API}/chat/regenerate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: conversation.conversation_id,
          message_id: messageId,
        }),
        
      });

      if (!response.ok) throw new Error("Failed to regenerate");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === "start") {
                setStreamingMessageId(data.message_id);
              } else if (data.type === "chunk") {
                fullResponseText += data.content;
                setStreamingContent(fullResponseText);
              } else if (data.type === "end") {
                const aiMessage = {
                  message_id: streamingMessageId || `msg_${Date.now()}`,
                  conversation_id: conversation.conversation_id,
                  role: "assistant",
                  content: fullResponseText,
                  timestamp: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, aiMessage]);
                setStreamingContent("");
                setStreamingMessageId(null);
                
                // Speak if voice mode
                if (voiceMode && fullResponseText) {
                  speakText(fullResponseText);
                }
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      toast.error("Failed to regenerate response");
    } finally {
      setIsStreaming(false);
    }
  };

  // Copy message
  const handleCopy = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  // File attachment indicator component
  const FileAttachment = ({ file, onRemove, showRemove = true }) => {
    const isImage = file.file_type === "image" || isImageType(file.content_type);
    
    return (
      <div className="flex items-center gap-2 bg-[#1E1E1E] border border-[#333] rounded-lg px-3 py-2 text-sm">
        {isImage ? (
          <Image className="w-4 h-4 text-[#14A800]" />
        ) : (
          <FileText className="w-4 h-4 text-[#14A800]" />
        )}
        <span className="text-[#E8E8E8] truncate max-w-[200px]">{file.filename}</span>
        <span className="text-[#666]">({formatFileSize(file.file_size || 0)})</span>
        {showRemove && (
          <button
            onClick={onRemove}
            className="ml-1 text-[#888] hover:text-white transition-colors"
            data-testid="remove-file-btn"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  // Voice mode indicator
  const VoiceModeIndicator = () => (
    <div className={`flex items-center gap-2 text-xs ${isSpeaking ? "text-[#14A800]" : "text-[#14A800]"}`}>
      <Volume2 className={`w-3 h-3 ${isSpeaking ? "animate-pulse" : ""}`} />
      <span>Voice mode active</span>
      {isSpeaking && <span className="animate-pulse">• Speaking...</span>}
    </div>
  );

  // Speaking animation overlay component
  const SpeakingAnimation = () => (
    <div className="fixed bottom-32 left-1/2 transform -translate-x-1/2 z-50 flex flex-col items-center gap-3 bg-[#1E1E1E]/95 backdrop-blur-lg rounded-2xl px-6 py-4 border border-[#14A800]/30 shadow-lg shadow-[#14A800]/10" data-testid="speaking-animation">
      {/* Sound wave bars */}
      <div className="flex items-center gap-1 h-8">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="w-1 bg-[#14A800] rounded-full animate-sound-wave"
            style={{
              animationDelay: `${i * 0.1}s`,
              height: '100%',
            }}
          />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-[#14A800] flex items-center justify-center">
          <span className="text-white font-bold text-xs font-serif">Z</span>
        </div>
        <span className="text-[#E8E8E8] text-sm font-medium">ZOEY is speaking...</span>
      </div>
      <button
        onClick={() => {
          if (audioPlayerRef.current) {
            audioPlayerRef.current.pause();
            audioPlayerRef.current = null;
          }
          setIsSpeaking(false);
        }}
        className="text-xs text-[#888] hover:text-white transition-colors"
        data-testid="stop-speaking-btn"
      >
        Click to stop
      </button>
    </div>
  );

  // Empty state - Home page design
  if (!conversation && messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col bg-[#0D0D0D]" data-testid="empty-chat">
        {/* Speaking Animation Overlay */}
        {isSpeaking && <SpeakingAnimation />}
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-8">
          <div className="w-full max-w-2xl text-center">
            <h1 className="text-2xl md:text-3xl font-light text-white mb-10" style={{ fontFamily: 'Georgia, serif' }}>
              Tell us what you need — we'll take it from here.
            </h1>

            {/* Input area */}
            <div className="w-full">
              <div className="relative bg-[#1a1a1a] rounded-xl">
                {/* Attached file indicator */}
                {attachedFile && (
                  <div className="px-5 pt-4">
                    <FileAttachment file={attachedFile} onRemove={handleRemoveFile} />
                  </div>
                )}
                
                {/* Voice mode indicator */}
                {voiceMode && (
                  <div className="px-5 pt-4">
                    <VoiceModeIndicator />
                  </div>
                )}
                
                <Textarea
                  ref={textareaRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                  placeholder={voiceMode ? "Click mic to speak or type here..." : "Tell us what problem you are trying to solve"}
                  className="min-h-[160px] max-h-[300px] bg-transparent border-none resize-none py-5 px-5 pr-20 text-white text-base placeholder:text-[#666666] focus:ring-0 rounded-xl"
                  data-testid="chat-input"
                />
                
                {/* Bottom buttons row */}
                <div className="absolute left-4 bottom-4 flex items-center gap-2">
                  {/* File upload button */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp,.gif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile || isStreaming}
                    className="w-10 h-10 p-0 bg-transparent hover:bg-[#333] border border-[#444] rounded-lg transition-colors"
                    data-testid="attach-file-btn"
                  >
                    {uploadingFile ? (
                      <Loader2 className="w-5 h-5 animate-spin text-[#888]" />
                    ) : (
                      <Paperclip className="w-5 h-5 text-[#888]" />
                    )}
                  </Button>
                  
                  {/* Voice toggle button */}
                  <Button
                    type="button"
                    onClick={voiceMode && !isRecording ? startRecording : voiceMode && isRecording ? stopRecording : toggleVoiceMode}
                    disabled={isTranscribing || isStreaming}
                    className={`w-10 h-10 p-0 rounded-lg transition-colors ${
                      voiceMode 
                        ? isRecording 
                          ? "bg-red-600 hover:bg-red-700 border-red-600 animate-recording-pulse" 
                          : "bg-[#14A800] hover:bg-[#108A00] border-[#14A800]"
                        : "bg-transparent hover:bg-[#333] border border-[#444]"
                    }`}
                    data-testid="voice-toggle-btn"
                  >
                    {isTranscribing ? (
                      <Loader2 className="w-5 h-5 animate-spin text-white" />
                    ) : isRecording ? (
                      <MicOff className="w-5 h-5 text-white" />
                    ) : (
                      <Mic className={`w-5 h-5 ${voiceMode ? "text-white" : "text-[#888]"}`} />
                    )}
                  </Button>
                </div>
                
                <Button
                  onClick={handleSend}
                  disabled={(!input.trim() && !attachedFile) || isStreaming}
                  className="absolute right-4 bottom-4 w-12 h-12 p-0 bg-[#14A800] hover:bg-[#108A00] disabled:bg-[#333333] disabled:opacity-50 rounded-full transition-colors"
                  data-testid="send-btn"
                >
                  <span className="text-white font-medium">Go</span>
                </Button>
              </div>
              
              {/* File type hint */}
              <p className="text-xs text-[#555] mt-2">
                <Paperclip className="w-3 h-3 inline mr-1" />
                Attach files or images (max {maxFileSizeMB}MB) • 
                <Mic className="w-3 h-3 inline mx-1" />
                Click mic for voice chat
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-6 text-center">
          <p className="text-[#555555] text-sm">
            Powered by Unizar
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-[#0D0D0D] overflow-hidden" data-testid="chat-area">
      {/* Speaking Animation Overlay */}
      {isSpeaking && <SpeakingAnimation />}
      
      {/* Messages */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 min-h-0">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-4">
          {messages.map((message, index) => (
            <MessageBubble
              key={message.message_id}
              message={message}
              onCopy={handleCopy}
              onRegenerate={handleRegenerate}
              isLast={index === messages.length - 1 && message.role === "assistant"}
            />
          ))}
          
          {/* Streaming message */}
          {isStreaming && streamingContent && (
            <MessageBubble
              message={{
                message_id: "streaming",
                role: "assistant",
                content: streamingContent,
                timestamp: new Date().toISOString(),
              }}
              isStreaming={true}
            />
          )}
          
          {/* Loading indicator */}
          {isStreaming && !streamingContent && (
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#14A800] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm font-serif">Z</span>
              </div>
              <div className="flex items-center gap-2 py-4">
                <Loader2 className="w-5 h-5 animate-spin text-[#14A800]" />
                <span className="text-[#A1A1AA] text-sm">ZOEY is thinking...</span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="border-t border-[#27272A] bg-[#0D0D0D]/80 backdrop-blur-lg p-5 pb-8">
        <div className="max-w-3xl mx-auto">
          {/* Attached file indicator */}
          {attachedFile && (
            <div className="mb-4">
              <FileAttachment file={attachedFile} onRemove={handleRemoveFile} />
            </div>
          )}
          
          {/* Voice mode indicator */}
          {voiceMode && (
            <div className="mb-4">
              <VoiceModeIndicator />
            </div>
          )}
          
          <div className="relative bg-[#171717] border border-[#27272A] rounded-2xl focus-within:border-[#14A800] focus-within:ring-1 focus-within:ring-[#14A800] transition-all duration-200 shadow-lg">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              placeholder={voiceMode ? "Click mic to speak or type here..." : "Message ZOEY..."}
              disabled={isStreaming}
              className="min-h-[140px] max-h-[280px] bg-transparent border-none resize-none py-6 px-20 pr-20 text-white text-base placeholder:text-[#666666] focus:ring-0 disabled:opacity-50"
              data-testid="chat-input"
            />
            
            {/* Left buttons */}
            <div className="absolute left-4 bottom-4 flex items-center gap-3">
              {/* File upload button */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp,.gif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/png,image/jpeg,image/webp,image/gif"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile || isStreaming}
                className="w-11 h-11 p-0 bg-transparent hover:bg-[#333] rounded-xl transition-colors"
                data-testid="attach-file-btn"
              >
                {uploadingFile ? (
                  <Loader2 className="w-5 h-5 animate-spin text-[#888]" />
                ) : (
                  <Paperclip className="w-5 h-5 text-[#888]" />
                )}
              </Button>
              
              {/* Voice toggle button */}
              <Button
                type="button"
                onClick={voiceMode && !isRecording ? startRecording : voiceMode && isRecording ? stopRecording : toggleVoiceMode}
                disabled={isTranscribing || isStreaming}
                className={`w-11 h-11 p-0 rounded-xl transition-colors ${
                  voiceMode 
                    ? isRecording 
                      ? "bg-red-600 hover:bg-red-700 animate-recording-pulse" 
                      : "bg-[#14A800] hover:bg-[#108A00]"
                    : "bg-transparent hover:bg-[#333]"
                }`}
                data-testid="voice-toggle-btn"
              >
                {isTranscribing ? (
                  <Loader2 className="w-5 h-5 animate-spin text-white" />
                ) : isRecording ? (
                  <MicOff className="w-5 h-5 text-white" />
                ) : (
                  <Mic className={`w-5 h-5 ${voiceMode ? "text-white" : "text-[#888]"}`} />
                )}
              </Button>
            </div>
            
            <Button
              onClick={handleSend}
              disabled={(!input.trim() && !attachedFile) || isStreaming}
              className="absolute right-4 bottom-4 w-12 h-12 p-0 bg-[#14A800] hover:bg-[#108A00] disabled:bg-[#27272A] disabled:opacity-50 rounded-xl transition-colors"
              data-testid="send-btn"
            >
              {isStreaming ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <Send className="w-6 h-6" />
              )}
            </Button>
          </div>
          <p className="text-xs text-[#666666] text-center mt-4">
            ZOEY can make mistakes. Consider checking important information.
          </p>
        </div>
      </div>
    </div>
  );
}
